import React, { useEffect, useMemo, useState } from 'react';
import { Form, Button, Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { sendContactMessage } from '../../api/requests';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ACTION = process.env.REACT_APP_RECAPTCHA_ACTION || 'contact';

const loadRecaptchaV3 = (siteKey) => {
    if (!siteKey) return Promise.resolve(false);
    if (typeof window === 'undefined') return Promise.resolve(false);
    if (window.grecaptcha && typeof window.grecaptcha.execute === 'function') return Promise.resolve(true);

    return new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-recaptcha-v3="true"]');
        if (existing) {
            resolve(true);
            return;
        }

        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.defer = true;
        script.dataset.recaptchaV3 = 'true';
        script.onload = () => resolve(true);
        script.onerror = () => reject(new Error('reCAPTCHA script failed to load'));
        document.head.appendChild(script);
    });
};

const executeRecaptchaV3 = (siteKey, action) => {
    if (!siteKey) return Promise.resolve('');
    if (!window.grecaptcha || typeof window.grecaptcha.ready !== 'function') {
        return Promise.reject(new Error('reCAPTCHA not available'));
    }

    return new Promise((resolve, reject) => {
        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute(siteKey, { action })
                .then((token) => resolve(token || ''))
                .catch((err) => reject(err));
        });
    });
};

const ContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [isHuman, setIsHuman] = useState(false);
    const [validated, setValidated] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const recaptchaEnabledInFrontend = useMemo(() => Boolean(RECAPTCHA_SITE_KEY), []);

    useEffect(() => {
        let isMounted = true;
        if (!recaptchaEnabledInFrontend) return;

        loadRecaptchaV3(RECAPTCHA_SITE_KEY).catch(() => {
            if (!isMounted) return;
            setSubmitError('reCAPTCHA konnte nicht geladen werden. Bitte später erneut versuchen.');
        });

        return () => {
            isMounted = false;
        };
    }, [recaptchaEnabledInFrontend]);

    const handleChange = (e) => {
        setSubmitError('');
        setSubmitSuccess(false);
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleCheckbox = (e) => {
        setSubmitError('');
        setSubmitSuccess(false);
        setIsHuman(e.target.checked);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;

        setValidated(true);
        setSubmitError('');
        setSubmitSuccess(false);

        if (form.checkValidity() === false || !isHuman) {
            e.stopPropagation();
            return;
        }

        try {
            setIsSubmitting(true);

            let recaptchaToken = '';
            if (recaptchaEnabledInFrontend) {
                await loadRecaptchaV3(RECAPTCHA_SITE_KEY);
                recaptchaToken = await executeRecaptchaV3(RECAPTCHA_SITE_KEY, RECAPTCHA_ACTION);
                if (!recaptchaToken) {
                    setSubmitError('reCAPTCHA-Überprüfung fehlgeschlagen. Bitte erneut versuchen.');
                    return;
                }
            }

            await sendContactMessage({
                name: formData.name,
                email: formData.email,
                nachricht: formData.message,
                recaptcha_token: recaptchaToken || undefined,
            });
            setSubmitSuccess(true);
            setFormData({ name: '', email: '', message: '' });
            setIsHuman(false);
            setValidated(false);
        } catch (error) {
            setSubmitError(
                'Senden fehlgeschlagen. Bitte später erneut versuchen oder per E-Mail kontaktieren.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container>
            <Row>
                <Col md={{ span: 6, offset: 3 }}>
                    {submitSuccess && (
                        <Alert variant="success">
                            Nachricht wurde gesendet. Vielen Dank!
                        </Alert>
                    )}
                    {submitError && <Alert variant="danger">{submitError}</Alert>}

                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        <Form.Group controlId="formBasicName">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Name eingeben"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Bitte geben Sie Ihren Namen an.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="formBasicEmail">
                            <Form.Label>Email-Adresse</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Email eingeben"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Bitte geben Sie eine gültige E-Mail-Adresse an.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="formBasicMessage">
                            <Form.Label>Nachricht</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Nachricht eingeben"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Bitte geben Sie eine Nachricht ein.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="formBasicHumanCheck" className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Ich bin kein Roboter"
                                checked={isHuman}
                                onChange={handleCheckbox}
                                isInvalid={validated && !isHuman}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Bitte bestätigen Sie, dass Sie kein Roboter sind.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Button variant="primary" type="submit" disabled={!isHuman || isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Sende…
                                </>
                            ) : (
                                'Absenden'
                            )}
                        </Button>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default ContactForm;
